import React, {useEffect} from 'react';
import {
  Box,
  Button,
  Typography,
  useMediaQuery,
  Chip,
} from '@mui/material';
import { theme } from '../Template';
import {ChromeReaderModeOutlined, PeopleAltOutlined, StarBorder } from '@mui/icons-material';
import IconWrapper from '../components/IconWrapper';
import ServiceCard from '../components/ServiceCard';
import ImgLazyLoad from 'src/screens/WhiteLabels/Templates/shared/components/ImgLazyLoad'
import moment from 'moment';
import useServiceDetails from '../../shared/logic/useServiceDetails';
import { makeStyles } from '@mui/styles';
import { PhotoPreview } from 'src/components/white-label-component/company-comp';
import { Rating } from '@mui/material';
import { calculateRating } from 'src/config/RatingCalculation';
import { useCompanyData } from '../../shared/contexts/DataContext';
import { createMarkup } from '../../shared/logic/Utils';
import imgProfile from '../../../../../assets/images/templates/profile.jpg';
import AppointmentBook from '../../shared/components/AppointmentBook/AppointmentBook';
import { serviceDetailsImage } from '../../shared/logic/imagesFns';
import useMyPagination from '../../shared/logic/useMyPagination';
import MyPagination from '../../shared/components/MyPagination';
import useRTL from 'src/hooks/useRTL';
import CustomMetaData from 'src/hooks/CustomMetaData';

const useRatingStyles = makeStyles(() => ({
  root:{
    fontSize:'1.5rem !important'
  },

  iconFilled:{
    color: '#0BDED6 !important',
  },

  iconHover:{
    color: '#0BDED6',
  },

  iconEmpty:{
    color: '#0BDED6 !important',
  },

  icon:{
    display:'unset !important'
  },

  descSpan:{
    '& li':{
      listStyle:'inherit'
    }
  }
}));

const ServiceDetails = () => {
  const matches = useMediaQuery(theme.breakpoints.up('md'));
  const { service, groups, reviews, videoEmbedSrc, photos, handleBooking, schedule } =
    useServiceDetails();
  const { id } = service || {};
  const { data } = useCompanyData();
  const { mainColor, t, companyData } = data || {};
  const [, ,  fontFamily] = useRTL();

    const [previewDialog, setPreviewDialog] = React.useState(false);
    const [activeImg, setActiveImg] = React.useState('');
    const [_fileType, setFileType] = React.useState('image');

  useEffect(()=> {
    window.scrollTo(0, 0);
  },[id])

  const previewEvent = (img,fileType) => {
    setPreviewDialog(true);
    setActiveImg(img);
    setFileType(fileType);
  };

  const closeDialog = () => {
    setPreviewDialog(false);
    setActiveImg('');
    setFileType('image');
  };

  return (
    <div>
      <CustomMetaData
        {...{
          title: `${companyData?.name} - ${service?.name}`,
          image: companyData && companyData.avatar,
          description: service?.description
        }}
      />
      <Header
        name={service?.name}
        cover={serviceDetailsImage(service)}
        photos={photos}
        video={videoEmbedSrc[0]}
        height="280px"
        transparencyColor={'#15476db2'}
        onClickEvent={()=> previewEvent(serviceDetailsImage(service),'image')}
      />
      <Overview service={service} t={t} reviews={reviews} fontFamily />

      {schedule ? <AppointmentWrapper schedule={schedule} serviceId={id} data={data} t={t} fontFamily={fontFamily}/> : null}
      {groups?.length ? <Groups groups={groups} handleBooking={handleBooking} t={t} /> : null}
      {reviews?.length > 0 && <Reviews reviews={reviews} t={t} data={data} matches={matches} />}

      <Box
      style={{width: '90%',margin: 'auto',paddingTop: '24px',paddingBottom: '24px'}}
      >
        <Box pb={2}>
          <Typography gutterBottom style={{ fontWeight: 700,fontSize:'1.5rem' }} variant="h6">
          {t && t('companyPage.relatedService')}
          </Typography>
        </Box>
        <RelatedServices
          services={data.products ? data.products.filter(v=> v.id !== id) : []}
          t={t}
          data={data}
          mainColor={mainColor}
        />
      </Box>

      {previewDialog && activeImg ? <PhotoPreview dataArray={[{path:activeImg,type:_fileType}]} closeEvent={closeDialog} /> : null}
    </div>
  );
};

const Header = ({ name, photos, video, cover,onClickEvent }) => {
  return (
    <Box
      pb={4}
      height={{ xs: 'auto', lg: '380px' }}
      width="90%"
      minHeight="400px"
      m="auto"
      display="flex"
      flexDirection={{ xs: 'column-reverse', lg: 'row' }}
      justifyContent="space-between"
      {...(onClickEvent ? {onClick:onClickEvent} : {})}
    >
      {photos?.length > 0 && (
        <Box
          width={{ xs: '100%', lg: '18%' }}
          height="100%"
          display={{ xs: 'none', md: 'flex' }}
          flexDirection={{ xs: 'row', lg: 'column' }}
          justifyContent="space-around"
        >
          {photos?.splice(0, 3)?.map((item, i) => (
            <Box
              key={i}
              width="100%"
              height="220px"
              borderRadius="15px"
              overflow="hidden"
              mr={1}
              mb={1}
            >
              <ImgLazyLoad>
                <img
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  src={item?.photoPath}
                  alt={name || 'gallery'}
                />
              </ImgLazyLoad>
            </Box>
          ))}
        </Box>
      )}
      <Box
        width={{ xs: '100%', lg: '81%' }}
        mx={photos?.length > 0 ? '0' : 'auto'}
        mb={{ xs: 2, lg: 0 }}
      >
        <Box width="100%" height="100%" borderRadius="15px" overflow="hidden">
          {video ? (
            <iframe
              width="100%"
              height="100%"
              src={video}
              title={name}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <ImgLazyLoad>
              <img
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                src={cover}
                alt={name || 'gallery'}
              />
            </ImgLazyLoad>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const Overview = ({ service, reviews, t, fontFamily }) => {
  const matches = useMediaQuery(theme.breakpoints.up('md'));
  const classes = useRatingStyles();

  return (
    <Box width="90%" m="auto">
      <Box
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        py={2}
      >
        <Typography
          variant={matches ? 'h4' : 'h6'}
          component="h2"
          style={{ fontWeight: 700,textTransform:'capitalize' }}
        >
          {service?.name}
        </Typography>
        {service?.online && (
          <Chip
            style={{
              borderRadius: '5px',
              backgroundColor: 'white',
              color: '#0BDED6',
              fontWeight: 700,
              margin: '0 1em',
            }}
            label="Online"
          />
        )}
      </Box>
      <Box pb={2}>
        <Box display="flex" flexDirection="row" alignItems="center">
          <Rating
            classes={{
              iconHover:classes.iconHover,
              iconFilled:classes.iconFilled,
              iconEmpty:classes.iconEmpty,
              icon:classes.icon
            }}
            name="review-rating"
            value={Number(calculateRating(reviews))}
            precision={0.1}
            readOnly
            emptyIcon={<StarBorder fontSize="inherit" />}
          />
          <Typography style={{color:'#7E7E7E',padding:'0px 8px',fontFamily:'inherit'}}>{t && t('service.review',{count:reviews.length})}</Typography>
        </Box>
      </Box>
      <Box pb={2}>
        <Typography variant="body1" className={classes.descSpan} dangerouslySetInnerHTML={createMarkup(service?.shortDescription)}></Typography>
      </Box>
    </Box>
  );
};

const AppointmentWrapper = ({ schedule, data, t, fontFamily, serviceId }) => {
  return (
    <Box width="100%" pb={3}>
      <Box pb={2} width="90%" m="auto">
        <Typography gutterBottom style={{ fontWeight: 700,fontFamily:'inherit' }} variant="h6">
          {t && t('services.group-section.appTitle')}
        </Typography>
      </Box>
      <AppointmentBook schedule={schedule} serviceId={serviceId} data={data} t={t} fontFamily={fontFamily}/>   
    </Box>
  );
};

const Groups = ({ groups, handleBooking, t }) => {
  const { setPage, count, currentItems } = useMyPagination(groups, 4);

  return (
    <Box py={3} width="90%" m="auto">
      <Box pb={2}>
        <Typography gutterBottom style={{ fontWeight: 700 }} variant="h6">
          {t && t('services.group-section.title')}
        </Typography>
      </Box>
      <Box display="flex" justifyContent="center" flexWrap="wrap">
        {currentItems?.map((item, i) => (
          <GroupItem key={i} group={item} handleBooking={handleBooking} t={t} />
        ))}
        {groups?.length < 1 && (
          <Typography>{t && t('companyPage.sorryNoGroups')}</Typography>
        )}
      </Box>
      <MyPagination count={count} onChange={setPage} />
    </Box>
  );
};

const GroupItem = ({ group, handleBooking, t }) => {
  const currentTime = moment().unix();
  const offer = (group.offer && group.offerValidity > currentTime);
  const isConvertCurrency = group?.convertCurrency && group?.displayPrice && group?.displayCurrency;

  return (
    <Box mr={2} mb={2} width="250px" height="350px" position="relative">
      <Box
        height="90%"
        bgcolor="white"
        px={2}
        py={2}
        boxShadow="0 0 5px rgb(228, 228, 228)"
      >
        <Box pb={2} textAlign="center">
          <Typography
            variant="h6"
            color="textSecondary"
            style={{ textTransform: 'capitalize', fontWeight: 600 }}
          >
            {group.name}
          </Typography>

          <Typography
            variant="subtitle1"
            style={{
              textTransform: 'capitalize',
              color: '#9F9F9F',
              height: '50px',
            }}
          >
            {group.groupType === 'subscription' && t
              ? t(`companyPage.${group.subscription}`) +
                ' ' +
                t(`companyPage.${group.groupType}`)
              : t(`companyPage.${group.groupType}`)}
          </Typography>
        </Box>
        <Box pb={2}>
          <Box display="flex" alignItems="center" mr={3} mb={2}>
            <IconWrapper
              mx={1}
              width="30px"
              height="30px"
              bgcolor="transparent"
              border="1px solid grey"
            >
              <ChromeReaderModeOutlined style={{ fontSize: '1rem' }} />
            </IconWrapper>
            <div>
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                {group.subscription === 'custom period' ? group.numberDays : group.durationDays} {t && t('companyPage.sessions')}
              </Typography>
            </div>
          </Box>

          <Box display="flex" alignItems="center" mr={3} mb={2}>
            <IconWrapper
              mx={1}
              width="30px"
              height="30px"
              bgcolor="transparent"
              border="1px solid grey"
            >
              <PeopleAltOutlined style={{ fontSize: '1rem' }} />
            </IconWrapper>
            <div>
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                {group.availableSeats} {t && t('companyPage.availableSeats')}
              </Typography>
            </div>
          </Box>
        </Box>
        <Box display="flex" justifyContent="space-between">
          <Box display="flex">
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              {isConvertCurrency ?
              `${group.displayCurrency} ${group.displayPrice}` :
              `${group.currency} ${offer ? group.offerPrice : group.groupPrice}`}
            </Typography>
            {offer && (
              <Typography
                variant="subtitle1"
                style={{
                  textDecoration: 'line-through',
                  opacity: '0.5',
                  margin: '0 0.4em',
                }}
              >
                {group.currency} {group.groupPrice}
              </Typography>
            )}
          </Box>
          {offer && (
            <Typography
              variant="h6"
              color="textSecondary"
              style={{ fontWeight: 700 }}
            >
              {group.offer}
            </Typography>
          )}
        </Box>
        {isConvertCurrency ?
          <Typography
              variant="subtitle1"
            >
            {`Pay ${group?.groupPrice} ${group.currency}`}
          </Typography>
        : null}
      </Box>

      <Box position="absolute" width="100%" textAlign="center" bottom="-20px">
        <Button
          onClick={() => handleBooking(group.id)}
          disableElevation
          variant="contained"
          color="primary"
          size="large"
        >
          {t && t('companyPage.bookNow')}
        </Button>
      </Box>
    </Box>
  );
};

const Reviews = ({ reviews, t, data, matches }) => {
  const { setPage, count, currentItems } = useMyPagination(reviews, 3);

  return (
    <Box width="90%" m="auto" py={3}>
      <Box pb={2}>
        <Typography gutterBottom style={{ fontWeight: 700 }} variant="h6">
          {t && t('companyPage.reviews')}
        </Typography>
      </Box>
      <div>
        {currentItems?.map((item, i) => (
          <ReviewItem key={i} review={item} data={data} matches={matches} />
        ))}
      </div>
      <MyPagination count={count} onChange={setPage} />
    </Box>
  );
};

const ReviewItem = ({ review, data, matches }) => {
  const classes = useRatingStyles();

  return (
    <Box py={2} px={3} mb={2} boxShadow="0 0 5px rgb(228, 228, 228)">
      <Box py={2} display="flex" width="100%">
        <Box width="50px" height="50px" borderRadius="50%">
          <ImgLazyLoad>
            <img
              style={{ width: '100%', borderRadius: '50%' }}
              src={review.parentAvatar || imgProfile}
              alt={review.parentName || 'gallery'}
            />
          </ImgLazyLoad>
        </Box>

        <Box
          width="90%"
          pb={2}
          mx={2}
          display="flex"
          justifyContent="space-between"
        >
          <div>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {review.parentName || '---'}
            </Typography>
            <Typography variant="subtitle2">
              {review.reviewDate ? moment(new Date(review.reviewDate*1000)).format('D MMM YYYY') : '---'}
            </Typography>
          </div>
          <div>
            <Rating
                classes={{
                  iconHover:classes.iconHover,
                  iconFilled:classes.iconFilled,
                  iconEmpty:classes.iconEmpty,
                  icon:classes.icon
                }}
                name="review-rating"
                value={Number(review.rating)}
                precision={0.1}
                readOnly
                emptyIcon={<StarBorder fontSize="inherit" />}
              />
          </div>
        </Box>
      </Box>

      <Typography variant="body2">{review.comment || '---'}</Typography>

      { review.reply ? <ReviewReply review={review} data={data} matches={matches} /> : null }
    </Box>
  );
};

const ReviewReply = ({review, data,matches}) => {
  const { avatar, name } = data.companyData || {};
  return(
    <Box
      color="#15476D"
      py={3}
      px={3}
      mt={2}
      width={matches ? "calc(100% - 48px)" : "calc(100% - 48px)"}
      alignSelf="flex-end"
      bgcolor="#fff"
      border="1px solid #EFEFEF"
    >
      <Box mb={3} display="flex" justifyContent="space-between">
        <Box display="flex" alignItems="center">
          <Box width="70px" height="70px" borderRadius="50%" mr={2} style={{border:'1px solid rgb(239, 239, 239)'}}>
            <ImgLazyLoad>
            <img
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '50%',
              }}
              src={avatar || imgProfile}
              alt={name || 'profile'}
            />
            </ImgLazyLoad>
          </Box>
          <div>
            <Typography variant="subtitle1" style={{ fontWeight: 600, textTransform:'capitalize' }}>
              {name || '---'}
            </Typography>
            <Typography variant="subtitle2" color="textPrimary">
              {review.replyDate ? moment(new Date(review.replyDate*1000)).format('D MMM YYYY') : '---'}
            </Typography>
          </div>
        </Box>
      </Box>
      <Box>
        <Typography color="textPrimary">{review.reply}</Typography>
      </Box>
    </Box>
  )
};

const RelatedServices = ({ services, data }) => {
  const { setPage, count, currentItems } = useMyPagination(services, 3);

  const displayDescription = data.displayProductDesc !== undefined ? data.displayProductDesc : true;
  const companyId = data.companyData ? data.companyData.companyId : '';
  return (
    <Box pb={5} width="100%" margin="auto">
        <Box display="flex" justifyContent='center' flexWrap="wrap">
          {currentItems?.map((item, i) => (
            <ServiceCard key={i} service={item} {...{displayDescription,companyId}} />
          ))}
        </Box>

      <MyPagination count={count} onChange={setPage} />
    </Box>
  );
};

export default ServiceDetails;
